import { useCallback, useEffect, useState } from 'react'
import { ReactComponent as PeopleIcon } from 'assets/icons/people.svg'
import { ReactComponent as BookIcon } from 'assets/icons/book.svg'
import { ReactComponent as PlanetIcon } from 'assets/icons/book.svg'
import { ReactComponent as ClockIcon } from 'assets/icons/clock.svg'
import axiosInstance from 'api/api'
import { dashboardStore } from '../models/dashboardStore'
import { fetchSessions } from 'api/sessions'
import { fetchAssignments } from 'api/assigments'

const buildKpiListFromStats = (stats = null) => [
	{
		id: 1,
		color: 'green',
		label: '',
		value: stats?.active_students_this_week ?? stats?.active_students_today ?? 0,
		icon: <PeopleIcon />,
		subtitle: 'with activity',
		info: 'Active Students This Week',
	},
	{
		id: 2,
		color: 'purpleDark',
		label: '',
		value: stats?.students_exploring_careers ?? 0,
		icon: <BookIcon />,
		subtitle: 'this week',
		info: 'Students Exploring Careers',
	},
	{
		id: 3,
		color: 'blue',
		label: '',
		value: stats?.students_exploring_countries ?? 0,
		icon: <PlanetIcon />,
		subtitle: stats?.country_breakdown ?? '0',
		info: 'Students Exploring Countries',
	},
	{
		id: 4,
		color: 'red',
		label: '',
		value: stats?.upcoming_deadlines ?? 0,
		icon: <ClockIcon />,
		subtitle: 'due soon',
		info: 'Upcoming Deadlines',
	},
]

const useDashboardPage = () => {
	const [sessions, setSessions] = useState([])
	const [assignments, setAssignments] = useState([])
	const [dashboardStats, setDashboardStats] = useState(() => dashboardStore.kpiStatsCache || {})
	const [isLoadingKpis, setIsLoadingKpis] = useState(
		() => !dashboardStore.kpisFetchedOnce,
	)

	const loadSessions = useCallback(async () => {
		try {
			const response = await fetchSessions()
			setSessions(response.sessions || [])
		} catch (err) {
			console.error('Failed to load sessions:', err)
			setSessions([])
		}
	}, [])

	const loadAssignments = useCallback(async () => {
		try {
			const response = await fetchAssignments()
			setAssignments(response.assignments || [])
		} catch (err) {
			console.error('Failed to load assignments:', err)
			setAssignments([])
		}
	}, [])

	useEffect(() => {
		loadSessions()
		loadAssignments()
	}, [loadSessions, loadAssignments])

	const [kpiList, setKpiList] = useState(() =>
		buildKpiListFromStats(dashboardStore.kpiStatsCache),
	)

	useEffect(() => {
		if (dashboardStore.kpisFetchedOnce) {
			setKpiList(buildKpiListFromStats(dashboardStore.kpiStatsCache))
			setIsLoadingKpis(false)
			return
		}
		let cancelled = false
		setIsLoadingKpis(true)
		const loadDashboardStats = async () => {
			try {
				const response = await axiosInstance.get('/counseling/dashboard/stats')
				if (cancelled) return
				const stats = response.data
				dashboardStore.setKpiStatsCache(stats)
				dashboardStore.setKpisFetchedOnce(true)
				setDashboardStats(stats || {})
				setKpiList(buildKpiListFromStats(stats))
			} catch (err) {
				console.error('Error loading dashboard stats:', err)
				if (!cancelled) dashboardStore.setKpisFetchedOnce(true)
			} finally {
				if (!cancelled) setIsLoadingKpis(false)
			}
		}

		loadDashboardStats()
		return () => {
			cancelled = true
		}
	}, [])

	return {
		kpiList,
		dashboardStats,
		tableTabs: dashboardStore.tableTabs,
		quickFilters: dashboardStore.quickFilters,
		activeTableTab: dashboardStore.activeTableTab,
		activeQuickFilter: dashboardStore.activeQuickFilter,
		setActiveTableTab: dashboardStore.setActiveTableTab,
		setActiveQuickFilter: dashboardStore.setActiveQuickFilter,

		rows: dashboardStore.rows,
		sessions,
		assignments,
		refetchSessions: loadSessions,
		isLoadingKpis,
		isLoadingTable: false,
		tasksFetchedOnce: dashboardStore.tasksFetchedOnce,
		tasksLastResponseWasEmpty: dashboardStore.tasksLastResponseWasEmpty,
	}
}

export default useDashboardPage
